import React, { useState, forwardRef } from 'react';
import DayPickerInput from "react-day-picker/DayPickerInput";
import 'react-day-picker/lib/style.css';
import * as Icon from 'react-feather'
import moment from 'moment';
import styles from '../../../DataDashboard.module.scss';

const WeeksSelector = ({ selectedDays, setSelectedDays, firstDayOfWeek }) => {

    const [hoverRange, setHoverRange] = useState(undefined);
    const [anchorWeek, setAnchorWeek] = useState(null);

    const getLastCompletedWeekEnd = () => {
        const startOfCurrentWeek = moment().startOf('week');
        const lastCompletedWeekEnd = startOfCurrentWeek.subtract(1, 'day').toDate();
        return lastCompletedWeekEnd;
    }

    const getWeekBoundaries = (date) => {
        return {
            from: moment(date).startOf('week').toDate(),
            to: moment(date).endOf('week').toDate(),
        };
    }

    const getDaysInRange = (startDate, endDate) => {
        const days = [];
        let current = moment(startDate);
        const end = moment(endDate);
        
        while (current.isSameOrBefore(end, 'day')) {
            days.push(current.toDate());
            current.add(1, 'day');
        }
        
        return days;
    }

    const getWeeksDifference = (date1, date2) => {
        const week1Start = moment(date1).startOf('week');
        const week2Start = moment(date2).startOf('week');
        return Math.abs(week2Start.diff(week1Start, 'weeks'));
    }

    const checkDateAllowed = date => {
        const lastCompletedWeekEnd = getLastCompletedWeekEnd();
        return moment(date).isSameOrBefore(lastCompletedWeekEnd, 'day');
    }

    const handleDayChange = date => {
        if (!checkDateAllowed(date)) return;
        
        const clickedWeekBoundaries = getWeekBoundaries(date);
        
        if (!anchorWeek) {
            setAnchorWeek(clickedWeekBoundaries.from);
            setSelectedDays(getDaysInRange(clickedWeekBoundaries.from, clickedWeekBoundaries.to));
        } else {
            const anchorWeekStart = moment(anchorWeek);
            const clickedWeekStart = moment(clickedWeekBoundaries.from);
            
            if (clickedWeekStart.isAfter(anchorWeekStart)) {
                const weeksDiff = getWeeksDifference(anchorWeek, clickedWeekBoundaries.from);
                
                if (weeksDiff < 3) {
                    setSelectedDays(getDaysInRange(anchorWeek, clickedWeekBoundaries.to));
                } else {
                    setAnchorWeek(clickedWeekBoundaries.from);
                    setSelectedDays(getDaysInRange(clickedWeekBoundaries.from, clickedWeekBoundaries.to));
                }
            } else {
                setAnchorWeek(clickedWeekBoundaries.from);
                setSelectedDays(getDaysInRange(clickedWeekBoundaries.from, clickedWeekBoundaries.to));
            }
        }
    };

    const onPrevWeek = () => {
        const currentStart = moment(selectedDays[0]);
        const newStart = currentStart.subtract(7, 'days');
        const newEnd = moment(newStart).endOf('week');
        
        setAnchorWeek(newStart.toDate());
        setSelectedDays(getDaysInRange(newStart.toDate(), newEnd.toDate()));
    }
    
    const onNextWeek = () => {
        const currentStart = moment(selectedDays[0]);
        const newStart = currentStart.add(7, 'days');
        const newEnd = moment(newStart).endOf('week');
        
        const lastCompletedWeekEnd = getLastCompletedWeekEnd();
        if (moment(newEnd).isSameOrBefore(lastCompletedWeekEnd, 'day')) {
            setAnchorWeek(newStart.toDate());
            setSelectedDays(getDaysInRange(newStart.toDate(), newEnd.toDate()));
        }
    }
    
    const modifiers = {
        hoverRange: hoverRange,
        selectedRange: selectedDays.length > 0 && {
            from: selectedDays[0],
            to: selectedDays[selectedDays.length - 1],
        },
        hoverRangeStart: hoverRange && hoverRange.from,
        hoverRangeEnd: hoverRange && hoverRange.to,
        selectedRangeStart: selectedDays.length > 0 && selectedDays[0],
        selectedRangeEnd: selectedDays.length > 0 && selectedDays[selectedDays.length - 1],
        disabled: {
            after: getLastCompletedWeekEnd()
        },
    };

    const CustomInput = ({ onClick, onFocus, onBlur }, ref) => (
        <button ref={ref} className={styles.inputCalendarBtn} onFocus={onFocus} onBlur={onBlur} onClick={onClick}>
            {selectedDays.length > 0 ? (
                <>
                    {moment(selectedDays[0]).format('ll')} – {' '}
                    {moment(selectedDays[selectedDays.length - 1]).format('ll')}
                </>
            ) : 'Choose dates'}
        </button>
    )

    return (
        <div className={styles.subFiltersDates}>
            <button 
                className={styles.subFiltersWeekNav} 
                onClick={onPrevWeek}
            >
                <Icon.ChevronLeft />
            </button>
            
            <div className="week-calendar-wrapper">
                <DayPickerInput
                    component={forwardRef(CustomInput)}
                    dayPickerProps={{
                        firstDayOfWeek: firstDayOfWeek,
                        selectedDays,
                        modifiers,
                        showOutsideDays: true,
                        month: selectedDays.length > 0 ? moment(selectedDays[0]).toDate() : moment().toDate(),
                        toMonth: moment().toDate(),
                        onDayClick: handleDayChange,
                        onDayMouseEnter: (date) => {
                            if (!checkDateAllowed(date)) {
                                setHoverRange(undefined);
                                return;
                            }
                            
                            const hoveredWeekBoundaries = getWeekBoundaries(date);
                            
                            if (anchorWeek) {
                                const anchorWeekStart = moment(anchorWeek);
                                const hoveredWeekStart = moment(hoveredWeekBoundaries.from);
                                
                                if (hoveredWeekStart.isSameOrAfter(anchorWeekStart)) {
                                    const weeksDiff = getWeeksDifference(anchorWeek, hoveredWeekBoundaries.from);
                                    
                                    if (weeksDiff < 3) {
                                        setHoverRange({
                                            from: anchorWeek,
                                            to: hoveredWeekBoundaries.to
                                        });
                                    } else {
                                        setHoverRange({
                                            from: hoveredWeekBoundaries.from,
                                            to: hoveredWeekBoundaries.to
                                        });
                                    }
                                } else {
                                    setHoverRange({
                                        from: hoveredWeekBoundaries.from,
                                        to: hoveredWeekBoundaries.to
                                    });
                                }
                            } else {
                                setHoverRange({
                                    from: hoveredWeekBoundaries.from,
                                    to: hoveredWeekBoundaries.to
                                });
                            }
                        },
                        onDayMouseLeave: () => setHoverRange(undefined)
                    }}
                />
            </div>
            
            <button 
                type='button' 
                className={styles.subFiltersWeekNav} 
                onClick={onNextWeek}
                disabled={moment(selectedDays[selectedDays.length - 1]).add(7, 'days').isAfter(getLastCompletedWeekEnd(), 'day')}
            >
                <Icon.ChevronRight />
            </button>
        </div>
    );
}

export default WeeksSelector;